import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BookOpenCheck, ClipboardCheck, Search } from "lucide-react";
import { api } from "../../lib/api";
import { useDebouncedValue } from "../../lib/utils";
import {
  AdminPageHeader,
  Callout,
  Input,
  Metric,
  Panel,
  Select,
  StateChip,
  errorMessage,
  formatDate,
  humanize,
} from "./client-operations-shared";

type QueueOperation = {
  operationId: string;
  companyId: string;
  companyName: string;
  lifecycle: string | null;
  state: string;
  workflowVersion: number;
  generation: number;
  partyStatus: string | null;
  onboardingStatus: string | null;
  collectionVariant: string;
  checkoutEnabled: boolean;
  transactionId: string | null;
  subscriptionId: string | null;
  invoiceNumber: string | null;
  dueAt: string | null;
  lastErrorCode: string | null;
  resolvedAt: string | null;
  updatedAt: string;
  activeUserCount: number;
  blocker: string | null;
};

type QueueResponse = {
  paddleConfigured: boolean;
  paddleEnvironment: string | null;
  operations: QueueOperation[];
};

const GROUPS = ["all", "draft", "ready", "awaiting_payment", "attention", "complete"] as const;

export function OnboardingQueue() {
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState<(typeof GROUPS)[number]>("all");
  const debouncedSearch = useDebouncedValue(search.trim().toLowerCase(), 200);
  const queueQ = useQuery({ queryKey: ["admin", "onboarding"], queryFn: () => api<QueueResponse>("/admin/onboarding") });
  const all = queueQ.data?.operations ?? [];
  const visible = useMemo(() => all.filter((operation) => {
    if (debouncedSearch && !`${operation.companyName} ${operation.invoiceNumber ?? ""} ${operation.transactionId ?? ""}`.toLowerCase().includes(debouncedSearch)) return false;
    return group === "all" || operationGroup(operation) === group;
  }), [all, debouncedSearch, group]);
  const counts = useMemo(() => ({
    ready: all.filter((operation) => operationGroup(operation) === "ready").length,
    payment: all.filter((operation) => operationGroup(operation) === "awaiting_payment").length,
    attention: all.filter((operation) => operationGroup(operation) === "attention").length,
  }), [all]);

  return (
    <div className="space-y-5">
      <AdminPageHeader
        eyebrow="Commercial operations"
        title="Client onboarding"
        description="A resumable queue for contracting, Paddle invoice-party setup, first-invoice review, payment monitoring, and technical activation."
        actions={<Link to="/admin/onboarding-guide" className="inline-flex min-h-8 items-center gap-1.5 rounded-md border border-border bg-bg px-3 py-1.5 text-xs font-medium text-fg transition-colors hover:border-accent/50"><BookOpenCheck size={13} aria-hidden="true" /> Open onboarding guide</Link>}
      />
      {queueQ.data && !queueQ.data.paddleConfigured && <Callout tone="warning" title="Paddle is not configured">Local client work remains available, but preview, transaction, and invoice actions are disabled.</Callout>}
      <div className="grid gap-3 sm:grid-cols-3">
        <Metric label="Ready for action" value={counts.ready} tone={counts.ready ? "good" : "neutral"} detail="Preview, prepare, or issue" />
        <Metric label="Awaiting payment" value={counts.payment} detail="Invoice issued; access is a separate state" />
        <Metric label="Needs reconciliation" value={counts.attention} tone={counts.attention ? "danger" : "neutral"} detail="No blind retry is permitted" />
      </div>
      <Panel>
        <div className="ops-toolbar ops-admin-filter-bar flex flex-wrap gap-2 border-b border-border/70 p-3">
          <label className="relative min-w-64 flex-1"><Search size={14} className="pointer-events-none absolute left-2.5 top-2.5 text-muted" /><Input className="pl-8" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search client, invoice, or transaction" aria-label="Search onboarding" /></label>
          <Select className="w-48" value={group} onChange={(event) => setGroup(event.target.value as typeof group)} aria-label="Filter onboarding state">
            {GROUPS.map((value) => <option key={value} value={value}>{humanize(value)}</option>)}
          </Select>
        </div>
        {queueQ.isLoading ? <div className="p-8 text-center text-sm text-muted">Loading onboarding operations…</div>
          : queueQ.isError ? <div className="p-4"><Callout tone="danger" title="Onboarding queue could not be loaded">{errorMessage(queueQ.error)}</Callout></div>
          : visible.length === 0 ? <div className="p-10 text-center"><ClipboardCheck size={24} className="mx-auto text-muted" /><div className="mt-2 text-sm font-medium">No operations in this queue</div><p className="mt-1 text-xs text-muted">New client records start here without granting access or touching Paddle.</p></div>
          : <div className="ops-admin-table-scroll overflow-x-auto"><table className="w-full min-w-[980px] text-left text-xs"><thead className="border-b border-border bg-border/10 text-[11px] uppercase tracking-wide text-muted"><tr><th className="p-3 font-medium">Client</th><th className="p-3 font-medium">Stage</th><th className="p-3 font-medium">Invoice party</th><th className="p-3 font-medium">Purchase</th><th className="p-3 font-medium">Due</th><th className="p-3 font-medium">Access/setup</th><th className="p-3 font-medium">Blocker</th><th className="p-3 text-right font-medium">Open</th></tr></thead><tbody>{visible.map((operation) => <tr key={operation.operationId} className="border-b border-border/50 last:border-0 hover:bg-border/10"><td className="p-3"><Link to="/admin/clients/$clientId" params={{ clientId: operation.companyId }} className="font-medium hover:text-accent">{operation.companyName}</Link><div className="mt-0.5 text-[11px] text-muted">Generation {operation.generation} · {queueQ.data?.paddleEnvironment ? queueQ.data.paddleEnvironment.toUpperCase() : "LOCAL"}</div></td><td className="p-3"><StateChip value={operation.state} /></td><td className="p-3"><StateChip value={operation.partyStatus} /></td><td className="p-3"><StateChip value={operation.onboardingStatus} />{operation.invoiceNumber && <div className="mt-1 font-mono text-[10px] text-muted">{operation.invoiceNumber}</div>}</td><td className="p-3">{formatDate(operation.dueAt)}</td><td className="p-3"><StateChip value={operation.lifecycle} /><div className="mt-1 text-[11px] text-muted">{operation.activeUserCount} active users</div></td><td className="max-w-56 p-3 text-[11px] text-muted">{operation.blocker ? humanize(operation.blocker) : "—"}</td><td className="p-3 text-right"><Link to="/admin/onboarding/$operationId" params={{ operationId: operation.operationId }} className="inline-flex items-center gap-1 font-medium text-accent hover:underline">Resume <ArrowRight size={13} /></Link></td></tr>)}</tbody></table></div>}
      </Panel>
    </div>
  );
}

function operationGroup(operation: QueueOperation): (typeof GROUPS)[number] {
  if (operation.resolvedAt || ["paid", "complete", "abandoned", "checkout_abandoned", "invoice_canceled"].includes(operation.state)) return "complete";
  if (operation.lastErrorCode || ["issue_uncertain", "reconciliation_required", "overdue", "onboarding_reversed"].includes(operation.state)) return "attention";
  if (["issued_waiting_webhooks", "awaiting_payment"].includes(operation.state)) return "awaiting_payment";
  if (["commercial_ready", "party_ready", "preview_ready", "transaction_ready", "checkout_ready"].includes(operation.state)) return "ready";
  return "draft";
}
